import { ArrowRight } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { CATEGORY_DEFAULT_CTA, CATEGORY_ICONS, CATEGORY_LABELS } from "./constants";
import { trackAdvertiser } from "./trackAdvertiser";

export function SponsorCard({ advertiser, onInquire, compact, outline, className }) {
  const advertiserId = advertiser?.id;
  const tracked = useRef(false);
  useEffect(() => {
    if (!advertiserId || tracked.current) return;
    tracked.current = true;
    trackAdvertiser(advertiserId, "view");
  }, [advertiserId]);
  if (!advertiser) return null;
  const hasLink = Boolean(advertiser.destination_url);
  const handleClick = () => {
    trackAdvertiser(advertiser.id, "click");
    if (!hasLink) onInquire?.(advertiser);
  };
  const ctaLabel = advertiser.button_label || CATEGORY_DEFAULT_CTA[advertiser.category] || "Contact Business";
  const linkProps = hasLink ? { href: advertiser.destination_url, target: "_blank", rel: "noopener noreferrer" } : {};
  const Tag = hasLink ? "a" : "button";

  if (compact) {
    const Icon = CATEGORY_ICONS[advertiser.category] || CATEGORY_ICONS.financing;
    return (
      <div data-testid={`sponsor-card-${advertiser.id}`} className="rounded-xl bg-[#F8F7F4] p-5 flex flex-col items-center text-center gap-2.5 h-full">
        <span className="h-11 w-11 rounded-full bg-[#C7A361] grid place-items-center"><Icon size={19} className="text-[#111820]" strokeWidth={1.75} /></span>
        <span className="text-[10px] font-mono uppercase tracking-[.1em] text-[#6B6356]">{CATEGORY_LABELS[advertiser.category] || advertiser.category}</span>
        <Link to={`/local-experts/${advertiser.slug}`} data-testid={`sponsor-card-profile-link-${advertiser.id}`} className="font-medium text-[#202833] text-sm hover:text-[#C7A361] transition-colors">{advertiser.business_name}</Link>
        <Tag data-testid={`sponsor-card-cta-${advertiser.id}`} {...linkProps} onClick={handleClick} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#C7A361] hover:text-[#101828] transition-colors">{ctaLabel}<ArrowRight size={13} /></Tag>
      </div>
    );
  }
  return (
    <div data-testid={`sponsor-card-${advertiser.id}`} className={`rounded-xl border border-[#E7E4DE] bg-white overflow-hidden flex flex-col h-full ${className || ""}`}>
      <Link to={`/local-experts/${advertiser.slug}`} data-testid={`sponsor-card-profile-link-${advertiser.id}`}>
        {(advertiser.cover_photo_url || advertiser.logo_url) ? <img src={advertiser.cover_photo_url || advertiser.logo_url} alt="" className="w-full aspect-[16/10] object-cover" /> : <div className="w-full aspect-[16/10] bg-[#F8F7F4] grid place-items-center text-3xl font-serif-display text-[#667085]">{advertiser.business_name?.[0] || "?"}</div>}
      </Link>
      <div className="p-5 flex-1 flex flex-col">
        <span className="text-[10px] font-mono uppercase tracking-[.14em] text-[#C7A361]">{CATEGORY_LABELS[advertiser.category] || advertiser.category}</span>
        <Link to={`/local-experts/${advertiser.slug}`} className="font-serif-display text-lg text-[#202833] mt-1 hover:text-[#C7A361] transition-colors">{advertiser.business_name}</Link>
        {advertiser.description && <p className="text-sm text-[#667085] mt-1.5 flex-1">{advertiser.description}</p>}
        <Tag data-testid={`sponsor-card-cta-${advertiser.id}`} {...linkProps} onClick={handleClick} className={outline
          ? "mt-4 inline-flex items-center justify-center gap-1.5 rounded-full border border-[#C7A361] text-[#202833] text-xs font-semibold uppercase tracking-[0.08em] py-2.5 hover:bg-[#F8F7F4] transition"
          : "mt-4 inline-flex items-center justify-center rounded-full bg-[#C7A361] text-[#111820] text-xs font-semibold uppercase tracking-[0.08em] py-2.5 hover:brightness-95 transition"}>{ctaLabel}{outline && <ArrowRight size={13} />}</Tag>
        {advertiser.disclosure_text && <p className="text-[10px] text-[#94A3B8] leading-snug mt-2">{advertiser.disclosure_text}</p>}
      </div>
    </div>
  );
}
